'use client';
import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import Link from 'next/link';
import Image from 'next/image';

const machineData = [
    {
        name: "Advanced Digital Printing Presses",
        description: "Our state-of-the-art digital presses offer unmatched flexibility for short to medium runs, ensuring vibrant colors and variable data capability without the need for plates.",
        features: [
            "Variable Data Printing (VDP)",
            "Expanded Color Gamut (up to 7 colors)",
            "No Minimum Order Quantity",
            "Instant Setup & Quick Turnaround"
        ],
        image: "/images/infrastructure/machine-2.jpg",
        reverse: false
    },
    {
        name: "High-Speed Flexographic Machines",
        description: "For high-volume production, our flexo lines provide consistent quality at incredible speeds. Ideal for long runs in FMCG and industrial sectors.",
        features: [
            "Speeds up to 150m/min",
            "In-line die-cutting and laminating",
            "UV and Water-based inks",
            "High-precision registration control"
        ],
        image: "/images/infrastructure/machine-1.jpg",
        reverse: true
    },
    {
        name: "UV, Foiling & Embossing Units",
        description: "Transform your labels into premium brand assets with our advanced finishing capabilities. We add texture, shine, and depth that commands attention.",
        features: [
            "Spot and Flood UV Varnish",
            "Hot and Cold Foil Stamping",
            "3D Embossing & Debossing",
            "Silk Screen Effects"
        ],
        image: "/images/infrastructure/uv-foiling.jpg",
        reverse: false
    },
    {
        name: "AI-Powered Color Optimization",
        description: "Our integrated AI systems monitor color consistency in real-time, ensuring that every label across a million-unit run matches your brand guidelines perfectly.",
        features: [
            "Spectral Color Measurement",
            "AI-assisted Ink Mixing",
            "Real-time Defect Detection",
            "Cloud-based Color Profiles"
        ],
        image: "/images/infrastructure/color-system.png",
        reverse: true
    }
];

const StandardMachine = ({ machine, index }) => {
    return (
        <div className={`flex flex-col ${machine.reverse ? 'md:flex-row-reverse' : 'md:flex-row'} items-center gap-12 md:gap-20 py-24 px-4 sm:px-6 lg:px-8 max-w-[1440px] mx-auto`}>
            <motion.div
                className="flex-1 w-full"
                initial={{ opacity: 0, x: machine.reverse ? 50 : -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
            >
                <div className="relative aspect-[4/3] rounded-2xl overflow-hidden group border border-gray-200 shadow-[0_20px_50px_rgba(0,0,0,0.08)] hover:shadow-[0_30px_70px_rgba(0,0,0,0.12)] transition-all duration-300">
                    <div className="absolute inset-0 bg-black/5 group-hover:bg-transparent transition-colors duration-500 z-10" />
                    <Image
                        src={machine.image}
                        alt={machine.name}
                        fill
                        className="object-cover transform group-hover:scale-105 transition-transform duration-700"
                    />
                </div>
            </motion.div>

            <motion.div
                className="flex-1 text-center md:text-left"
                initial={{ opacity: 0, x: machine.reverse ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
            >
                <h2 className="text-3xl md:text-5xl font-display font-bold text-[#09172e] mb-6">
                    {machine.name}
                </h2>
                <p className="text-gray-800 text-lg mb-8 leading-relaxed font-medium">
                    {machine.description}
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left">
                    {machine.features.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-start gap-3 justify-center sm:justify-start">
                            <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#2e8b8d] flex-shrink-0" />
                            <span className="text-gray-900 text-sm font-semibold">{feature}</span>
                        </li>
                    ))}
                </ul>

            </motion.div>
        </div>
    );
};

const CinematicMachine = ({ machine }) => {
    const containerRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start end", "end start"]
    });

    const scale = useTransform(scrollYProgress, [0, 0.4, 0.7, 1], [0.6, 0.9, 1.05, 1.15]);
    const y = useTransform(scrollYProgress, [0, 1], [100, -50]);
    const rotateX = useTransform(scrollYProgress, [0, 0.5, 1], [10, 5, 0]);
    const opacity = useTransform(scrollYProgress, [0, 0.2, 0.9, 1], [0.6, 1, 1, 0.6]);

    return (
        <div ref={containerRef} className="relative w-full min-h-[140vh] overflow-hidden flex flex-col items-center justify-center py-20 perspective-[1200px]">
            {/* White Background - Matching Theme */}
            <div className="absolute inset-0 bg-white z-0" />

            {/* Cinematic Machine Content */}
            <div className="relative z-10 w-full max-w-[1440px] px-4 flex flex-col items-center gap-12">

                {/* 3D Floating Image */}
                <motion.div
                    style={{
                        scale,
                        y,
                        rotateX,
                        opacity,
                        transformStyle: 'preserve-3d'
                    }}
                    className="relative w-full max-w-5xl aspect-[16/9] shadow-[0_30px_100px_rgba(0,0,0,0.12)] rounded-3xl overflow-hidden border border-gray-200 bg-[#f8f9fa]"
                >
                    <Image
                        src={machine.image}
                        alt={machine.name}
                        fill
                        className="object-cover"
                        priority
                    />
                </motion.div>

                {/* Text Content - Reveals as image stabilizes */}
                <motion.div
                    style={{ opacity: useTransform(scrollYProgress, [0.3, 0.5], [0, 1]) }}
                    className="text-center max-w-4xl"
                >
                    <h2 className="text-4xl md:text-6xl font-display font-bold text-[#09172e] mb-6 drop-shadow-sm">
                        {machine.name}
                    </h2>
                    <p className="text-gray-800 text-lg md:text-xl leading-relaxed mb-8 max-w-2xl mx-auto font-medium">
                        {machine.description}
                    </p>
                    <div className="flex flex-wrap justify-center gap-4 md:gap-8">
                        {machine.features.map((feature, i) => (
                            <div key={i} className="flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-full border border-gray-200">
                                <div className="w-1.5 h-1.5 rounded-full bg-[#2e8b8d]" />
                                <span className="text-gray-900 text-sm font-semibold">{feature}</span>
                            </div>
                        ))}
                    </div>


                </motion.div>
            </div>
        </div>
    );
};

const Machinery = () => {
    return (
        <section className="bg-white">
            {machineData.map((machine, index) => {
                // Target the "Center" machine - typically the Flexographic one (index 1) which is large horizontal
                if (index === 1) {
                    return (
                        <React.Fragment key={index}>
                            <div className="block md:hidden">
                                <StandardMachine machine={machine} index={index} />
                            </div>
                            <div className="hidden md:block">
                                <CinematicMachine machine={machine} />
                            </div>
                        </React.Fragment>
                    );
                } else {
                    return <StandardMachine key={index} machine={machine} index={index} />;
                }
            })}
        </section>
    );
};

export default Machinery;
